'use client';
// VIDEO_SEULE_V1 (16/09/2026, demande Gaetan) — présenter UNE vidéo dans une
// page, intégrée : aperçu net (YouTube maxresdefault avec repli), lecture EN
// PLACE au clic (aucun changement de page ni d'URL), position gauche / centre /
// droite, largeur réglable, titre / description / compteurs (vues, ♥)
// affichables ou masquables. Réutilise les sources (lib/videoSource) et les
// compteurs (/api/vitrine/videos/*) de la galerie, sans toucher à celle-ci.
import { useEffect, useRef, useState } from 'react';
import { parseVideoSource, autoThumbnail, embedUrl } from '@/lib/videoSource';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'https://api.ligobox.com';

function fmt(n) {
  n = Number(n || 0);
  if (n >= 1000000) return `${(n / 1000000).toFixed(1).replace('.0', '')} M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace('.0', '')} k`;
  return String(n);
}

// Vignette la plus nette possible : YouTube maxresdefault (1280×720) puis
// repli hqdefault (480×360) si la vidéo n'a pas de rendu HD.
function posterCandidates(source, video) {
  if (video.thumbnail_url) return [video.thumbnail_url];
  if (source.kind === 'youtube') {
    return [`https://i.ytimg.com/vi/${source.id}/maxresdefault.jpg`,
            `https://i.ytimg.com/vi/${source.id}/hqdefault.jpg`];
  }
  const t = autoThumbnail(source, video);
  return t ? [t] : [];
}

export default function VideoSingleBlock({ config = {} }) {
  const video = (config.videos || [])[0];
  const source = video ? parseVideoSource(video.url) : { kind: 'none' };
  const align = ['left', 'center', 'right'].includes(config.align) ? config.align : 'center';
  const maxWidth = Math.max(320, Math.min(1400, parseInt(config.single_width, 10) || 900));
  const showTitle = config.show_title !== false;
  const showDesc = config.show_description !== false;
  const showStats = config.show_stats !== false;
  const [playing, setPlaying] = useState(false);
  const [posterIdx, setPosterIdx] = useState(0);
  const [stats, setStats] = useState({});
  const [liked, setLiked] = useState(false);
  const [ratio, setRatio] = useState('16 / 9');
  const vidRef = useRef(null);

  useEffect(() => {
    if (!video?.id) return;
    fetch(`${API_URL}/api/vitrine/videos/stats?ids=${video.id}`, { cache: 'no-store' })
      .then(r => r.json()).then(d => setStats((d?.stats || {})[video.id] || {})).catch(() => {});
    try { setLiked(!!JSON.parse(localStorage.getItem('ligobox_video_likes') || '{}')[video.id]); } catch (_) {}
  }, [video?.id]);

  // Format réel des lecteurs embarqués (même service que la galerie).
  useEffect(() => {
    if (!video?.url || source.kind === 'file') return;
    fetch(`${API_URL}/api/vitrine/videos/embed-ratio?url=${encodeURIComponent(video.url)}`)
      .then(r => r.json())
      .then(d => { if (d && d.width && d.height) setRatio(`${d.width} / ${d.height}`); })
      .catch(() => {});
  }, [video?.url, source.kind]);

  if (!video || source.kind === 'none') return null;

  const play = () => {
    setPlaying(true);
    try {
      const key = `ligobox_video_viewed_${video.id}`;
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1');
        fetch(`${API_URL}/api/vitrine/videos/${video.id}/view`, { method: 'POST' })
          .then(r => r.json()).then(d => setStats(s => ({ ...s, views: d.views }))).catch(() => {});
      }
    } catch (_) {}
  };

  const toggleLike = () => {
    const next = !liked;
    setLiked(next);
    try {
      const all = JSON.parse(localStorage.getItem('ligobox_video_likes') || '{}');
      if (next) all[video.id] = true; else delete all[video.id];
      localStorage.setItem('ligobox_video_likes', JSON.stringify(all));
    } catch (_) {}
    fetch(`${API_URL}/api/vitrine/videos/${video.id}/like`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ unlike: !next }),
    }).then(r => r.json()).then(d => setStats(s => ({ ...s, likes: d.likes }))).catch(() => {});
  };

  const posters = posterCandidates(source, video);
  const poster = posters[Math.min(posterIdx, posters.length - 1)] || null;
  const justify = align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center';
  const textAlign = align;

  return (
    <section style={{ padding: 'calc(var(--section-py, 100px) * 0.6) 0' }}>
      <div className="container-page">
        {(config.title || config.subtitle) && (
          <div style={{ textAlign, marginBottom: 32 }}>
            {config.title && <h2 style={{ margin: '0 0 10px', color: 'var(--color-text)' }}>{config.title}</h2>}
            {config.subtitle && <p style={{ margin: 0, color: 'var(--color-text-muted)', fontSize: 17 }}>{config.subtitle}</p>}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: justify }}>
          <div style={{ width: '100%', maxWidth, textAlign }}>
            <div style={{
              position: 'relative', width: '100%', aspectRatio: ratio, overflow: 'hidden',
              background: '#0f172a', boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
            }}>
              {!playing ? (
                <button type="button" onClick={play} aria-label={`Lire la vidéo${video.title ? ` : ${video.title}` : ''}`}
                  style={{ position: 'absolute', inset: 0, border: 'none', padding: 0, cursor: 'pointer',
                           background: '#0f172a', display: 'block', width: '100%', height: '100%' }}>
                  {poster && (
                    <img src={poster} alt={video.title || 'Vidéo'}
                      onError={() => setPosterIdx(i => i + 1)}
                      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  )}
                  <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{
                      width: 78, height: 78, borderRadius: '50%', background: 'var(--color-primary, #e27228)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '0 6px 24px rgba(0,0,0,0.35)',
                    }}>
                      <svg width="30" height="30" viewBox="0 0 24 24" fill="white" aria-hidden="true"><path d="M8 5.14v13.72L19 12 8 5.14z" /></svg>
                    </span>
                  </span>
                  {video.duration && (
                    <span style={{ position: 'absolute', right: 10, bottom: 10, background: 'rgba(0,0,0,0.8)', color: 'white',
                                   fontSize: 12, fontWeight: 600, padding: '2px 7px', borderRadius: 5 }}>{video.duration}</span>
                  )}
                </button>
              ) : source.kind === 'file' ? (
                <video ref={vidRef} controls autoPlay playsInline poster={video.thumbnail_url || undefined}
                  onLoadedMetadata={(e) => { const w = e.currentTarget.videoWidth, h = e.currentTarget.videoHeight; if (w && h) setRatio(`${w} / ${h}`); }}
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block' }}>
                  {video.url_webm && <source src={video.url_webm} type="video/webm" />}
                  <source src={video.url} type={/\.webm(\?.*)?$/i.test(video.url) ? 'video/webm' : 'video/mp4'} />
                  Votre navigateur ne supporte pas la lecture vidéo.
                </video>
              ) : (
                <iframe src={embedUrl(source)} title={video.title || 'Vidéo'}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                  allowFullScreen
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0, display: 'block' }} />
              )}
            </div>
            {(showTitle && video.title) || (showDesc && video.description) || showStats ? (
              <div style={{ padding: '18px 4px 0' }}>
                {showTitle && video.title && (
                  <div style={{ fontWeight: 700, fontSize: 20, color: 'var(--color-text)', lineHeight: 1.3, marginBottom: 8 }}>{video.title}</div>
                )}
                {showDesc && video.description && (
                  <p style={{ margin: '0 0 10px', fontSize: 15.5, lineHeight: 1.6, color: 'var(--color-text-muted)', whiteSpace: 'pre-line' }}>{video.description}</p>
                )}
                {showStats && (
                  <div style={{ fontSize: 13, color: 'var(--color-text-muted)', display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                    <span>{fmt(stats.views)} vue{(stats.views || 0) > 1 ? 's' : ''}</span>
                    <button type="button" onClick={toggleLike} aria-pressed={liked}
                      style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13,
                               color: liked ? '#dc2626' : 'var(--color-text-muted)', display: 'inline-flex', alignItems: 'center', gap: 4, padding: 0 }}>
                      <span aria-hidden="true">{liked ? '♥' : '♡'}</span> {fmt(stats.likes)}
                    </button>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}
